<script setup lang="ts">
    import { storeToRefs } from 'pinia';
    import { useHipSpinePreferencesViewModel } from '@/views/preferences/hipSpine/useHipSpinePreferencesViewModel';
    import type { FieldErrorKind } from '@/views/preferences/hipSpine/hipSpinePreferencesValidation';
    import { PREFERENCES_TARGETS } from '@/domain/analytics/analyticsTargets';
    import {
        MAX_ANTEVERSION,
        MAX_INCLINATION,
        MIN_ANTEVERSION,
        MIN_INCLINATION,
    } from '@/domain/anatomy/pelvis/anteversionInclination';

    const vm = useHipSpinePreferencesViewModel();
    const { current, hasUnsavedChanges, canSave, notificationState, saveError, isReady, isSaving } =
        storeToRefs(vm);
    const { save, fieldErrorKind } = vm;

    function fieldErrorMessage(field: string, kind: FieldErrorKind): string {
        const angle = field.endsWith('InclinationBound') ? 'Inclination' : 'Anteversion';
        if (kind === 'inverted') {
            return `${angle} lower bound must be less than the upper bound. Reduce it or raise the upper bound.`;
        }
        if (angle === 'Inclination') {
            return `Inclination must be between ${MIN_INCLINATION}° and ${MAX_INCLINATION}°`;
        }
        return `Anteversion must be between ${MIN_ANTEVERSION}° and ${MAX_ANTEVERSION}°`;
    }

    function fieldErrorMessages(field: string): string {
        const kind = fieldErrorKind(field);
        return kind === null ? '' : fieldErrorMessage(field, kind);
    }
</script>

<template>
    <template v-if="isReady">
        <app-notification
            v-if="notificationState === 'success'"
            data-testid="hip-spine-notification"
            variant="success"
            title="Your Hip-Spine preferences were successfully updated!"
        />
        <app-notification
            v-else-if="notificationState === 'error' && saveError"
            data-testid="hip-spine-notification"
            variant="error"
            :title="saveError.title"
            :message="saveError.message"
        />

        <app-form-section-title>Hip-Spine</app-form-section-title>
        <app-form-subtitle class="mb-2" :data-analytics-id="PREFERENCES_TARGETS.hipSpineIntro">
            Define your preferred Inclination and Anteversion ranges below. This will adjust results
            in the Hip-Spine Guide and nomogram on the 3D Planner Page. To generate a Hip-Spine
            analysis, upload hip-spine X-rays under Case Settings
        </app-form-subtitle>

        <v-container class="hip-spine-preferences-container">
            <div class="d-flex flex-column gap-2">
                <app-form-group
                    label="Inclination lower bound"
                    class="input-field"
                    data-testid="inclination-min"
                >
                    <app-number-input
                        v-model="current.minCupInclinationBound"
                        :step="1"
                        :error="!!fieldErrorMessages('minCupInclinationBound')"
                    />
                </app-form-group>
                <p
                    v-if="fieldErrorMessages('minCupInclinationBound')"
                    class="text-error error-message"
                >
                    {{ fieldErrorMessages('minCupInclinationBound') }}
                </p>
            </div>

            <div class="d-flex flex-column gap-2">
                <app-form-group
                    label="Inclination upper bound"
                    data-testid="inclination-max"
                    class="input-field"
                >
                    <app-number-input
                        v-model="current.maxCupInclinationBound"
                        :step="1"
                        :error="!!fieldErrorMessages('maxCupInclinationBound')"
                    />
                </app-form-group>
                <p
                    v-if="fieldErrorMessages('maxCupInclinationBound')"
                    class="text-error error-message"
                >
                    {{ fieldErrorMessages('maxCupInclinationBound') }}
                </p>
            </div>

            <div class="d-flex flex-column gap-2">
                <app-form-group
                    label="Anteversion lower bound"
                    data-testid="anteversion-min"
                    class="input-field"
                >
                    <app-number-input
                        v-model="current.minCupAnteversionBound"
                        :step="1"
                        :error="!!fieldErrorMessages('minCupAnteversionBound')"
                    />
                </app-form-group>
                <p
                    v-if="fieldErrorMessages('minCupAnteversionBound')"
                    class="text-error error-message"
                >
                    {{ fieldErrorMessages('minCupAnteversionBound') }}
                </p>
            </div>

            <div class="d-flex flex-column gap-2">
                <app-form-group
                    label="Anteversion upper bound"
                    data-testid="anteversion-max"
                    class="input-field"
                >
                    <app-number-input
                        v-model="current.maxCupAnteversionBound"
                        :step="1"
                        :error="!!fieldErrorMessages('maxCupAnteversionBound')"
                    />
                </app-form-group>
                <p
                    v-if="fieldErrorMessages('maxCupAnteversionBound')"
                    class="text-error error-message"
                >
                    {{ fieldErrorMessages('maxCupAnteversionBound') }}
                </p>
            </div>
        </v-container>

        <app-form-actions :data-analytics-id="PREFERENCES_TARGETS.hipSpineSaveButton">
            <app-form-action-notification
                v-if="hasUnsavedChanges"
                data-testid="hip-spine-has-changes-warning"
                message="You have unsaved hip-spine changes"
            />
            <app-button-primary
                height="55"
                class="px-8"
                width="350"
                data-testid="hip-spine-submit-button"
                :loading="isSaving"
                :disabled="!canSave || isSaving"
                @click="save"
                >Save Hip-Spine Preferences
            </app-button-primary>
        </app-form-actions>
    </template>
</template>

<style scoped lang="scss">
    .hip-spine-preferences-container {
        display: flex;
        flex-direction: row;
        gap: 20px;
        flex-wrap: wrap;
        justify-content: center;
        align-items: center;
    }

    .input-field {
        width: 300px;
    }

    .error-message {
        font-size: 14px;
        position: relative;
        top: -10px;
        max-width: 300px;
    }
</style>
